import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell
} from 'recharts';
import { toast } from 'react-toastify';
import { apiCall } from '../services/api';
import Icon from '../components/Icon';
import {
  GreetingHero, FilterBar, KpiTile, SectionCard, ActivityFeed, CHART_COLORS, greetingFor
} from '../components/dashboard/DashboardKit';

const hrAPI = {
  getStats: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/hr/dashboard/stats${query ? '?' + query : ''}`);
  }
};

const STATUS_META = [
  { key: 'pending', label: 'Pending', color: '#f59e0b' },
  { key: 'underReview', label: 'Under Review', color: '#3b82f6' },
  { key: 'approved', label: 'Approved', color: '#10b981' },
  { key: 'rejected', label: 'Rejected', color: '#ef4444' },
  { key: 'onboarded', label: 'Onboarded', color: '#8b5cf6' }
];

export default function HRAnalyticsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const stored = JSON.parse(localStorage.getItem('user') || '{}');
  const displayName = stored.full_name || stored.firstName || stored.username || stored.email || 'there';
  const firstName = String(displayName).split(" ")[0];

  const load = async (r = range, cs = customStart, ce = customEnd) => {
    setLoading(true);
    try {
      const params = r === 'custom' ? (cs && ce ? { startDate: cs, endDate: ce } : { range: 'all' }) : { range: r };
      const res = await hrAPI.getStats(params);
      setData(res);
    } catch (e) {
      toast.error('Failed to load HR dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const changeRange = (r) => { setRange(r); load(r, customStart, customEnd); };
  const applyCustom = () => {
    if (!customStart || !customEnd) return toast.error('Select both dates');
    setRange('custom'); load('custom', customStart, customEnd);
  };

  const st = data?.stats || {};
  const statusData = STATUS_META.map((m) => ({ name: m.label, value: st[m.key] || 0, color: m.color }));

  if (loading && !data) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: 64, height: 64, fontSize: 28, margin: '0 auto 24px' }} />
          <h3 style={{ color: '#64748b', fontWeight: 600 }}>Loading HR Dashboard…</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content admin-analytics">
      <GreetingHero greeting={greetingFor(new Date().getHours())} name={firstName}
        actions={<><button className="btn btn-primary btn-sm" onClick={() => load()}><Icon name="refresh" /> Refresh</button></>} />

      <FilterBar range={range} onRange={changeRange} customStart={customStart} setCustomStart={setCustomStart}
        customEnd={customEnd} setCustomEnd={setCustomEnd} onApplyCustom={applyCustom} />

      <div className="kpi-grid">
        <KpiTile label="Total Applications" value={st.total || 0} icon={<Icon name="users" />} color="#037b90" />
        <KpiTile label="Pending" value={st.pending || 0} icon={<Icon name="clock" />} color="#f59e0b" />
        <KpiTile label="Under Review" value={st.underReview || 0} icon={<Icon name="search" />} color="#3b82f6" />
        <KpiTile label="Approved" value={st.approved || 0} icon={<Icon name="check" />} color="#10b981" />
        <KpiTile label="Onboarded" value={st.onboarded || 0} icon={<Icon name="user-check" />} color="#8b5cf6" />
      </div>

      <div className="dash-grid">
        <SectionCard title="Applications by Status" subtitle="Current pipeline breakdown">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={statusData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {statusData.map((e, i) => (<Cell key={i} fill={e.color} />))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>

        <SectionCard title="Recent Applications" subtitle="Latest recruiter applications">
          <ActivityFeed
            items={(data?.recentApplications || []).map((a) => ({
              id: a.id, title: a.full_name, message: a.email, status: a.status, created_at: a.created_at
            }))}
            emptyText="No recent applications." />
        </SectionCard>
      </div>
    </div>
  );
}
